// The Calendar's Day view: which hours its grid shows, how much of a day an
// event covers, and how blocks that overlap share the width. Pure functions of
// minutes from midnight, so the view only turns them into pixels.

import type { DateStr } from '@/types'
import { addDays, dateTimeFrom } from './dates'

/** A block on the grid, in minutes from the day's midnight. */
export interface Span {
  key: string
  start: number
  end: number
}

/** A block with its column among the blocks it overlaps: `col` of `cols`, side by side. */
export interface PlacedSpan extends Span {
  col: number
  cols: number
}

const DAY_MINUTES = 24 * 60

const clockMinutes = (d: Date) => d.getHours() * 60 + d.getMinutes()

/**
 * The part of an event that falls on `day`, or null when none does. Read from
 * the local clock rather than a difference in milliseconds, so a day when the
 * clocks change still runs from 00:00 to 24:00.
 */
export function spanOnDay(start: Date, end: Date, day: DateStr): { start: number; end: number } | null {
  const dayStart = dateTimeFrom(day, '00:00')
  const dayEnd = dateTimeFrom(addDays(day, 1), '00:00')
  if (end <= dayStart || start >= dayEnd) {
    // a moment-long event exactly at midnight still belongs to the day it starts
    if (!(start.getTime() === end.getTime() && start >= dayStart && start < dayEnd)) return null
  }
  const from = start <= dayStart ? 0 : clockMinutes(start)
  const to = end >= dayEnd ? DAY_MINUTES : clockMinutes(end)
  return { start: from, end: Math.max(from, to) }
}

/**
 * The whole hours the grid shows: 07:00 to 23:00 unless something on the day
 * starts earlier or ends later, when the grid stretches to take it in.
 */
export function gridHours(spans: Pick<Span, 'start' | 'end'>[], from = 7, to = 23): { from: number; to: number } {
  let lo = from * 60
  let hi = to * 60
  for (const s of spans) {
    if (s.start < lo) lo = s.start
    if (s.end > hi) hi = s.end
  }
  return { from: Math.floor(lo / 60), to: Math.min(24, Math.ceil(hi / 60)) }
}

/**
 * Places overlapping blocks side by side. Each block is first stretched to
 * `minLength`, so two that would be drawn on top of each other count as
 * overlapping. Blocks that overlap, directly or through a chain, form a group
 * that shares the width in as few columns as fit; a block takes the leftmost
 * column free at its start.
 */
export function layoutSpans(spans: Span[], minLength = 0): PlacedSpan[] {
  const sorted = spans
    .map((s) => ({ ...s, end: Math.max(s.end, s.start + minLength) }))
    .sort((a, b) => a.start - b.start || b.end - a.end || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
  const out: PlacedSpan[] = []
  let group: { span: Span; col: number }[] = []
  let colEnds: number[] = []
  let groupEnd = -Infinity
  const flush = () => {
    for (const g of group) out.push({ ...g.span, col: g.col, cols: colEnds.length })
    group = []
    colEnds = []
  }
  for (const s of sorted) {
    if (group.length && s.start >= groupEnd) flush()
    let col = colEnds.findIndex((end) => end <= s.start)
    if (col === -1) {
      col = colEnds.length
      colEnds.push(s.end)
    } else {
      colEnds[col] = s.end
    }
    group.push({ span: s, col })
    groupEnd = group.length === 1 ? s.end : Math.max(groupEnd, s.end)
  }
  flush()
  return out
}
